import campaignLevelsConstants from '@utils/constants/campaignLevels';
import campaignTypesConstants from '@utils/constants/campaignTypes';
import { apiServiceCampaigns } from '@api';
import normalizeCampaignHotel from '@/data/response/normalizeCampaignHotel';
import normalizeCampaignOffer from '@/data/response/normalizeCampaignOffer';
import { LogService } from '@/services';
import { apiConstants } from '@/utils/constants';
import notificationUtils from '@/utils/notificationUtils';

const STORE_NAME = 'campaignDetails';

/*
 * How each Campaign Details grid maps its rows. GET /api/campaigns/{id} returns the three lists
 * under these same names. This is a plain module rather than storeFactories/factory, because the
 * factory can't switch campaigns mid-load: its ALL mode hands a second get the previous
 * campaign's rows, and its CUSTOM mode appends both responses.
 */
const SECTION_NORMALIZERS = {
    offers: normalizeCampaignOffer,
    includedHotels: normalizeCampaignHotel,
    excludedHotels: normalizeCampaignHotel,
};

// Stands in for the API error when a response can't be read: its 500 status gives the app's
// generic "Internal Error" toast.
const UNREADABLE_RESPONSE = Object.freeze({ status: apiConstants.STATUS.INTERNAL_SERVER_ERROR });

// Numbers each opening, so a response is applied only to the opening that asked for it, even when
// the same campaign was opened again meanwhile.
let lastRequestId = 0;

const emptySection = () => ({ list: [], loading: false, failed: false });

const getDefaultState = () => ({
    // The open campaign: its id, the type and level that decide which grids apply, and the number
    // of the opening that loads them.
    campaign: null,
    offers: emptySection(),
    includedHotels: emptySection(),
    excludedHotels: emptySection(),
});

const isTargetedEvents = (campaign) =>
    !!campaign && campaign.campaignType === campaignTypesConstants.TARGETED_EVENTS;

export default {
    name: STORE_NAME,
    namespaced: true,
    state: getDefaultState(),
    getters: {
        // Included Hotels applies to Hotel-level campaigns of every type (GAP-1419 E1).
        showsIncludedHotels: (state) =>
            !!state.campaign && state.campaign.level === campaignLevelsConstants.HOTEL,
        // Only TARGETED_EVENTS campaigns have Sellable Rates and VIP Rates (PO, GAP-1447).
        showsRateColumns: (state) => isTargetedEvents(state.campaign),
        // Excluded Hotels exists for TARGETED_EVENTS campaigns only, at any level (GAP-1448 AC1).
        showsExcludedHotels: (state) => isTargetedEvents(state.campaign),
        // The grids that apply to the open campaign. Campaign Offers always does (GAP-1419 E1).
        sections: (state, getters) => {
            if (!state.campaign) {
                return [];
            }

            return [
                'offers',
                ...(getters.showsIncludedHotels ? ['includedHotels'] : []),
                ...(getters.showsExcludedHotels ? ['excludedHotels'] : []),
            ];
        },
    },
    mutations: {
        setCampaign(state, campaign) {
            state.campaign = campaign;
        },
        setSection(state, { section, list, loading, failed }) {
            state[section] = { list, loading, failed };
        },
        clear(state) {
            Object.assign(state, getDefaultState());
        },
    },
    actions: {
        /*
         * Opens a Campaigns grid row's details and loads the grids that apply. Opening the
         * campaign that is already open loads it again only if its last load failed.
         */
        open({ state, getters, commit, dispatch }, { campaignId, campaignType, level }) {
            if (state.campaign && state.campaign.campaignId === campaignId) {
                return getters.sections.some((section) => state[section].failed)
                    ? dispatch('load')
                    : undefined;
            }

            lastRequestId += 1;
            commit('clear');
            commit('setCampaign', { campaignId, campaignType, level, requestId: lastRequestId });

            return dispatch('load');
        },
        close({ commit }) {
            commit('clear');
        },
        /*
         * Loads the open campaign once and fills every grid that applies from it. A response for
         * an earlier opening, or one that comes back after the details closed, is dropped, so a
         * slow answer never replaces the rows on screen. A response that can't be read counts as
         * a failure instead of leaving the grids loading.
         */
        async load({ state, getters, commit, dispatch }) {
            const { campaignId, requestId } = state.campaign;
            const sections = getters.sections;

            sections.forEach((section) =>
                commit('setSection', { section, list: [], loading: true, failed: false })
            );

            const response = await apiServiceCampaigns.campaigns.getOne(campaignId);

            if (!state.campaign || state.campaign.requestId !== requestId) {
                return;
            }

            // A failed call fails every grid. A list that can't be read fails only its own grid,
            // so the others still show their rows.
            const lists = {};
            const unreadable = new Set();

            if (response.success) {
                sections.forEach((section) => {
                    try {
                        lists[section] = (response.data[section] || []).map(
                            SECTION_NORMALIZERS[section]
                        );
                    } catch (exception) {
                        LogService.error(
                            `Unreadable ${section} for campaign ${campaignId}`,
                            exception
                        );
                        unreadable.add(section);
                    }
                });
            }

            let error = null;

            if (!response.success) {
                error = response;
            } else if (unreadable.size) {
                error = UNREADABLE_RESPONSE;
            }
            if (error) {
                dispatch('notification/error', notificationUtils.getFromApiResponse(error), {
                    root: true,
                });
            }
            sections.forEach((section) => {
                const failed = !response.success || unreadable.has(section);

                commit('setSection', {
                    section,
                    list: failed ? [] : lists[section],
                    loading: false,
                    failed,
                });
            });
        },
    },
};
